import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('pestControlProvider');
export interface PestControlProviderInfoRec {
  id: string;
  userId: string;
  companyName?: string;
  accountNumber?: string;
  technicianName?: string;
  customerServicePhone?: string;
  emergencyPhone?: string;
  website?: string;
  serviceFrequency?: string;
  treatmentType?: string;
  monthlyCost?: number;
  contractStartDate?: string;
  contractEndDate?: string;
  autopayEnabled?: boolean;
  paymentMethod?: string;
  notes?: string;
  username?: string;
  password?: string;
  isLocked?: boolean;
}

class PestControlProviderInfoService {
  async getAllPestControlProviderInfo(): Promise<PestControlProviderInfoRec[]> {
    const response = await http.get('/api/pest-control-provider-info');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch pest control provider information');
    }

    return response.data.data || [];
  }

  async getPestControlProviderInfoById(id: string): Promise<PestControlProviderInfoRec | null> {
    try {
      const response = await http.get(`/api/pest-control-provider-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch pest control provider information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching pest control provider information by ID:', error);
      return null;
    }
  }

  async createPestControlProviderInfo(pestControlProviderData: Partial<PestControlProviderInfoRec>): Promise<PestControlProviderInfoRec> {
    const response = await http.post('/api/pest-control-provider-info', pestControlProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save pest control provider information');
    }

    return response.data.data;
  }

  async updatePestControlProviderInfo(id: string, pestControlProviderData: Partial<PestControlProviderInfoRec>): Promise<PestControlProviderInfoRec> {
    const response = await http.patch(`/api/pest-control-provider-info/${id}`, pestControlProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update pest control provider information');
    }

    return response.data.data;
  }

  async deletePestControlProviderInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/pest-control-provider-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete pest control provider information');
    }
  }

  async lockPestControlProviderInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for pest control provider ID:', id);
    const response = await http.post(`/api/pest-control-provider-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock pest control provider information');
    }
  }

  async unlockPestControlProviderInfo(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for pest control provider ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/pest-control-provider-info/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock pest control provider information');
    }
  }

  async getPestControlProviderAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/pest-control-provider-info/${id}/access-grants`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch pest control provider access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching pest control provider access grants:', error);
      return [];
    }
  }

  async deletePestControlProviderAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/pest-control-provider-info/${id}/access-grants/${grantId}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const pestControlProviderInfoService = new PestControlProviderInfoService();